/**
 * BertaReplyPanel (#428) — top of the focal area in the default call
 * view. The visual home for what Berta just said.
 *
 * Replaces the old `replyPanelSplit` style block in TabletApp which
 * was:
 *   - too small (~clamp(16-20px) — hard for elderly users to read)
 *   - rendered ONLY during `speaking`/`idle` (vanished mid-listening,
 *     so the user couldn't re-read what they just heard while they
 *     thought about the answer)
 *   - positioned BELOW the YouSaidCard (Bas's #428 ask: above)
 *
 * Now: large readable type, persists across all post-greeting states
 * (replaced only when a new reply arrives), scrollable when long.
 *
 * Idle-state placeholder shown when there's no reply yet (first
 * connect, before greeting plays) so the area never looks empty.
 *
 * #1032 — this panel is now the SINGLE focal text region in tablet-
 * portrait: it also hosts the user's live transcript on their turn
 * (`showUser` + `userTranscript`, heading flips to "You"), replacing the
 * separate UserTranscriptRow that used to stack beneath it. On a small
 * tablet the two areas competed for height; one current-speaker slot
 * reclaims it. Decision logic lives in the pure `resolveFocalPanel`
 * helper below so it stays unit-testable.
 *
 * Pure presentation. State (`reply`, transcript, scroll-stick refs) lives
 * in TabletApp; we just take props.
 */
import React from 'react';
import { useLang } from '../i18n/LanguageContext';

const BRAND_ORANGE = '#E8845A';
const INK = '#2C2C2C';
const INK_SOFT = '#6b5d50';

// #441 Phase 2 — read tablet i18n strings for the idle placeholder.
// Defensive: returns null when LanguageProvider isn't in the tree
// (tests render the component bare). Component falls back to the
// hardcoded English strings in that case.
function _useStrings() {
  const ctx = useLang();
  return ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.bertareply ? ctx.t.tablet.bertareply : null;
}


// #1032 — pure decision for the unified "current-speaker" focal panel.
// Extracted so the heading/body selection is unit-testable without
// rendering (BertaReplyPanel itself calls the useLang hook, which can't be
// invoked bare). Returns the heading, the body text, and whether the body
// is the idle placeholder (which drives the muted placeholder style).
//
// Rule: when it's the user's turn (showUser AND a non-empty transcript) the
// single panel shows THEIR words under a "You" heading; otherwise it shows
// the companion's reply, or the idle placeholder, under her name. Turns
// alternate, so one slot follows whoever is currently active.
export function resolveFocalPanel({
  reply = '',
  userTranscript = '',
  showUser = false,
  companionName = 'Lena',
  youLabel = 'You',
  placeholder = '',
  showPlaceholder = false,
  // #1032 — when false, an empty reply renders a BLANK box rather than the
  // idle placeholder. The host sets this false once the call has produced a
  // reply, so the brief gap when Berta starts a new reply (old reply just
  // cleared, first sentence not yet streamed) shows empty — not the stale
  // prior reply (already cleared) and not the "Hello…" placeholder. Defaults
  // true so the pre-greeting window still shows the placeholder.
  allowPlaceholder = true,
}) {
  const trimmedUser = (userTranscript || '').trim();
  if (showUser && trimmedUser.length > 0) {
    return { heading: youLabel, body: trimmedUser, isPlaceholder: false };
  }
  const trimmedReply = (reply || '').trim();
  const hasReply = !showPlaceholder && trimmedReply.length > 0;
  if (hasReply) {
    return { heading: companionName, body: trimmedReply, isPlaceholder: false };
  }
  // Empty reply (or forced placeholder) in companion mode.
  if (showPlaceholder || allowPlaceholder) {
    return { heading: companionName, body: placeholder, isPlaceholder: true };
  }
  return { heading: companionName, body: '', isPlaceholder: false };
}

export default function BertaReplyPanel({
  reply = '',
  companionName = 'Lena',
  scrollRef = null,
  onScroll = null,
  // When true, the placeholder is shown instead of any reply (for the
  // brief window between WS connect and the greeting TTS arriving).
  showPlaceholder = false,
  // #1032 — current-speaker slot. When showUser is true and userTranscript
  // is non-empty, this one panel shows the user's live words under a "You"
  // heading; otherwise it shows the companion's reply. Lets tablet-portrait
  // collapse the old two stacked text areas (reply + UserTranscriptRow)
  // into a single full-height region that follows whoever is talking.
  userTranscript = '',
  showUser = false,
  // #1032 — see resolveFocalPanel. False after the first reply so empty
  // gaps render a blank box instead of the idle placeholder.
  allowPlaceholder = true,
}) {
  const s = _useStrings();
  const placeholder = (s && s.idlePlaceholder) || "Hello — I'm here whenever you'd like to chat.";
  const youLabel = (s && s.you) || 'You';
  const { heading, body, isPlaceholder } = resolveFocalPanel({
    reply,
    userTranscript,
    showUser,
    companionName,
    youLabel,
    placeholder,
    showPlaceholder,
    allowPlaceholder,
  });

  return (
    <div style={containerStyle}>
      <div style={labelStyle}>{heading}</div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={isPlaceholder ? placeholderBodyStyle : bodyStyle}
      >
        {body}
      </div>
    </div>
  );
}


const containerStyle = {
  width: '100%',
  // Bas 2026-05-08 wide-screen screenshot: on tablet-landscape the
  // focal area is ~920px (column 1280 − strip 360). Letting the
  // reply card fill that gives a 72/28 visual split that swallows
  // the strip and produces ~6-word lines of giant text. Cap the
  // reading column at 720 (≈ 40 char per line at 32px — ideal for
  // elderly readers) and centre it inside the focal area. The focal
  // area still claims its flex space so the layout doesn't reflow;
  // the whitespace on either side of the card balances visually
  // against the strip on the right (panel:strip = 720:360 = 2:1).
  maxWidth: 720,
  marginLeft: 'auto',
  marginRight: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(255, 255, 255, 0.6)',
  border: '1px solid rgba(232, 132, 90, 0.18)',
  borderRadius: 22,
  padding: '20px 28px',
  boxShadow: '0 4px 22px rgba(70, 50, 30, 0.06)',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const labelStyle = {
  flex: '0 0 auto',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: BRAND_ORANGE,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 12,
};

// Big, high-contrast body. clamp() keeps it readable on a small phone
// AND a 12" tablet without media queries. Line-height generous for
// elderly readability. Scrollable when content overflows.
//
// #852 — clamp(20, 3vw, 32) was ~22 px on the A9 (production tablet,
// ~720 px CSS-portrait viewport): below the comfortable arm's-length
// reading threshold for elderly users. Raise the floor and slope so
// the A9 lands ≥28 px while desktop / 12" tablet still hits 36-40 px
// on the upper end.
//   • new floor 28 px — guarantees readability on every supported
//     phone too;
//   • 4vw slope — at 720 px viewport this gives 28.8 px (= floor),
//     at 1024 px gives 41 px (capped at 40);
//   • new ceiling 40 px — was 32 (too small for desktop / landscape
//     12" where the reply column is centered in 720 px and would
//     happily host larger type).
const bodyStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  // #1032 — keep an over-scroll of this panel from chaining to the document
  // and triggering the browser's pull-to-refresh (which would reload the
  // page and drop the call). Pairs with the document-root overscroll lock
  // in TabletApp during a connected call.
  overscrollBehavior: 'contain',
  fontSize: 'clamp(28px, 4vw, 40px)',
  lineHeight: 1.4,
  color: INK,
  fontWeight: 500,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  WebkitOverflowScrolling: 'touch',
};

const placeholderBodyStyle = {
  ...bodyStyle,
  color: INK_SOFT,
  fontStyle: 'italic',
  fontWeight: 400,
  opacity: 0.75,
};
